import jsQR from 'jsqr';

/** Chrome's native barcode reader. TypeScript's DOM library doesn't declare it,
 *  and Safari and Firefox don't implement it — hence both the typing and the
 *  fallback below. */
interface DetectedBarcode {
  rawValue: string;
}
declare global {
  interface Window {
    BarcodeDetector?: new (init?: { formats?: string[] }) => {
      detect(source: ImageBitmapSource): Promise<DetectedBarcode[]>;
    };
  }
}

/** Read the first QR code in an image. Returns null when the image holds none.
 *  Throws only if the blob isn't a decodable image. */
export async function decodeQr(file: Blob): Promise<string | null> {
  const bitmap = await createImageBitmap(file);
  try {
    // Native first: it reads photographs shot at an angle, or with a fold across
    // the code, that jsQR gives up on.
    if (window.BarcodeDetector !== undefined) {
      try {
        const found = await new window.BarcodeDetector({ formats: ['qr_code'] }).detect(bitmap);
        if (found.length > 0) return found[0].rawValue;
      } catch {
        // Some browsers define BarcodeDetector with no platform barcode service
        // behind it, and detect() rejects. That says nothing about the image, so
        // it must fall through — letting it escape would report an unreadable
        // file for a good certificate and kill the feature on those browsers.
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (ctx === null) return null;
    ctx.drawImage(bitmap, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return jsQR(data, width, height)?.data ?? null;
  } finally {
    bitmap.close();
  }
}
